import { useCallback, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';

import { applyViewport } from '../redux/diagram/diagram.actions';
import { easeInOutCubic, interpolateViewport, isNegligibleViewportDelta } from '../utils/viewport/tween-viewport';

const TWEEN_DURATION_MS = 220;

const prefersReducedMotion = () =>
    typeof window !== 'undefined' &&
    window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;

// Animates the canvas viewport with one redux dispatch per frame — the same
// cost as a user pan/zoom event, which the canvas already handles with
// off-screen culling, so large diagrams behave like a normal drag.
export function useViewportTween() {
    const dispatch = useDispatch();
    const frameRef = useRef(null);

    const cancelTween = useCallback(() => {
        if (frameRef.current !== null) {
            cancelAnimationFrame(frameRef.current);
            frameRef.current = null;
        }
    }, []);

    useEffect(() => cancelTween, [cancelTween]);

    const tweenTo = useCallback((from, to, onComplete) => {
        cancelTween();
        if (prefersReducedMotion() || isNegligibleViewportDelta(from, to)) {
            dispatch(applyViewport(to.scale, to.x, to.y));
            if (onComplete) onComplete();
            return;
        }
        const anchorX = window.innerWidth / 2;
        const anchorY = window.innerHeight / 2;
        const start = performance.now();
        const step = (now) => {
            const t = Math.min((now - start) / TWEEN_DURATION_MS, 1);
            const v = interpolateViewport(from, to, easeInOutCubic(t), anchorX, anchorY);
            dispatch(applyViewport(v.scale, v.x, v.y));
            if (t < 1) {
                frameRef.current = requestAnimationFrame(step);
            } else {
                frameRef.current = null;
                if (onComplete) onComplete();
            }
        };
        frameRef.current = requestAnimationFrame(step);
    }, [dispatch, cancelTween]);

    return { tweenTo, cancelTween };
}
